import { useState } from 'react';
import { Button, Group, Input, Typography } from '@screentone/core';
import { JsonEditor } from 'json-edit-react';

import {
  QueryRuleFormKeyConfig,
  QueryRuleFormType
} from 'components/query-rules-form/components/query-rule-form/query-rule-configs/types';
import { GLOBAL_PUBLICATION_KEY, usePublicationSettings } from 'hooks/publication-settings';
import styles from './QueryRuleSchemaEditor.module.scss';

const VALID_FORM_TYPES = new Set<string>(Object.values(QueryRuleFormType));

interface Props {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  hideUnknownWarning?: boolean;
  globalQueryRuleSchema?: Record<string, unknown> | null;
  isReadOnly?: boolean;
}

const QueryRuleSchemaEditor = ({
  data,
  onChange,
  hideUnknownWarning = false,
  globalQueryRuleSchema,
  isReadOnly = false
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const { data: globalSettingsResp } = usePublicationSettings({ publicationKey: GLOBAL_PUBLICATION_KEY });
  const fetchedGlobalSchema = globalSettingsResp?.publicationSetting.queryRuleSchema as
    | Record<string, QueryRuleFormKeyConfig>
    | null
    | undefined;
  const globalSchema =
    (globalQueryRuleSchema as Record<string, QueryRuleFormKeyConfig> | null | undefined) ?? fetchedGlobalSchema;

  const existingKeys = new Set(Object.keys(data));
  const availableKeys = globalSchema
    ? Object.keys(globalSchema)
        .filter((k) => !existingKeys.has(k))
        .sort()
    : [];

  const unknownKeys = !hideUnknownWarning && globalSchema ? Object.keys(data).filter((k) => !(k in globalSchema)) : [];

  const invalidTypeKeys = Object.entries(data)
    .filter(([, entry]) => {
      const formType = (entry as Partial<QueryRuleFormKeyConfig>).queryRuleFormType;
      return formType !== undefined && !VALID_FORM_TYPES.has(formType);
    })
    .map(([k]) => k);

  const trimmed = inputValue.trim();
  const notInGlobalSchema = !hideUnknownWarning && !!globalSchema && trimmed.length > 0 && !(trimmed in globalSchema);
  const addDisabled = isReadOnly || !trimmed || trimmed in data || notInGlobalSchema;

  const addKey = () => {
    if (addDisabled) return;
    const formType = globalSchema?.[trimmed]?.queryRuleFormType ?? QueryRuleFormType.TEXT;
    onChange({ ...data, [trimmed]: { queryRuleFormType: formType } });
    setInputValue('');
  };

  return (
    <Group gap="xs" direction="column" fullWidth>
      <Group gap="sm" valign="center" fullWidth>
        <Group.Item flex>
          <Input
            placeholder="Add a key (e.g. Author, LiveDate)"
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !addDisabled) {
                e.preventDefault();
                addKey();
              }
            }}
            error={notInGlobalSchema}
            disabled={isReadOnly}
          />
        </Group.Item>
        <Button tertiary disabled={addDisabled} onClick={addKey}>
          Add
        </Button>
      </Group>
      {notInGlobalSchema && (
        <Typography variant="note" color="lava">
          <strong>{trimmed}</strong> is not in the global query rule schema — add it there first.
        </Typography>
      )}
      {availableKeys.length > 0 && (
        <details className={styles.knownKeys}>
          <summary>Known Allesseh keys ({availableKeys.length} available)</summary>
          <Typography variant="note">{availableKeys.join(', ')}</Typography>
        </details>
      )}
      <JsonEditor
        data={data}
        indent={2}
        setData={(value) => onChange(value as Record<string, unknown>)}
        restrictAdd={isReadOnly ? true : ({ level }) => level === 0}
        restrictEdit={isReadOnly}
        restrictDelete={isReadOnly}
        maxWidth="100%"
        collapse={1}
      />
      {invalidTypeKeys.length > 0 && (
        <Typography variant="note" color="lava">
          ⚠ Invalid queryRuleFormType on: {invalidTypeKeys.join(', ')}. Valid values: {[...VALID_FORM_TYPES].join(', ')}
          .
        </Typography>
      )}
      {unknownKeys.length > 0 && (
        <Typography variant="note" color="lava">
          ⚠ Keys not in global schema: {unknownKeys.join(', ')}. Remove and re-add them via global settings to fix.
        </Typography>
      )}
    </Group>
  );
};

export default QueryRuleSchemaEditor;
